import { formatarMoedaBR, formatarDataBR, formatarTelefone, isMenorDeIdade } from "../utils.js";

/**
 * Constrói a estrutura de dados para o componente FormReview no TCE.
 *
 * @param {object} params
 * @param {object} params.formData - Dados do formulário TCE
 * @param {string} [params.reviewRole="empresa"] - Papel do usuário ("estudante" ou "empresa")
 * @param {number|null} [params.idadeCalculada=null] - Idade calculada do estudante
 * @param {boolean} [params.isEstudante=false] - Se o usuário logado é estudante
 * @param {object} [params.horario={}] - Estrutura de horários (fixo/alternado)
 * @returns {Array<{ id: string, section: string, fields: Array<{ label: string, value: any, complete: boolean }> }>}
 */
export const buildReviewData = ({
  formData = {},
  reviewRole = "empresa",
  idadeCalculada = null,
  isEstudante = false,
  horario = {},
} = {}) => {
  const cpfLimpo = (
    formData.cpf_estudante_estagiario ||
    formData.cpf_estudante ||
    ""
  ).replace(/\D/g, "");

  const valorBolsaExibicao = formatarMoedaBR(formData.valor_bolsa_auxilio);
  const valorTransporteExibicao = formatarMoedaBR(
    formData.valor_bolsa_transporte,
  );

  // Quando o estudante acessa a conferência, exibir os Dados Escolares (e Responsável Legal se menor de idade)
  if (reviewRole === "estudante") {
    const estudanteSections = [];
    if (isMenorDeIdade(idadeCalculada)) {
      estudanteSections.push({
        id: "responsavel-legal",
        section: "Responsável Legal",
        fields: [
          {
            label: "Nome do Responsável",
            value: formData.nome_menor_idade,
            complete: !!formData.nome_menor_idade?.trim(),
          },
          {
            label: "Email do Responsável",
            value: formData.email_menor_idade,
            complete: !!formData.email_menor_idade?.trim(),
          },
          {
            label: "Telefone do Responsável",
            value: formatarTelefone(formData.telefone_menor_idade),
            complete: !!formData.telefone_menor_idade?.trim(),
          },
          {
            label: "CPF do Responsável",
            value: formData.cpf_menor_idade,
            complete: !!formData.cpf_menor_idade?.trim(),
          },
        ],
      });
    }

    const dadosEscolaresFields = [];
    if (formData.escola?.trim()) {
      dadosEscolaresFields.push({ label: "Escola/Faculdade", value: formData.escola, complete: true });
    }
    if (formData.unidade?.trim()) {
      dadosEscolaresFields.push({ label: "Unidade", value: formData.unidade, complete: true });
    }
    if (formData.curso?.trim()) {
      dadosEscolaresFields.push({ label: "Curso", value: formData.curso, complete: true });
    }
    if (formData.ano_periodo_serie?.trim()) {
      dadosEscolaresFields.push({ label: "Ano, período ou série", value: formData.ano_periodo_serie, complete: true });
    }
    if (formData.previsao_formatura?.trim()) {
      dadosEscolaresFields.push({ label: "Previsão de Formatura", value: formData.previsao_formatura, complete: true });
    }
    if (formData.turno?.trim()) {
      const turnoMap = {
        MATUTINO: "Matutino",
        VESPERTINO: "Vespertino",
        NOTURNO: "Noturno",
        INTEGRAL: "Integral",
      };
      dadosEscolaresFields.push({ label: "Turno", value: turnoMap[formData.turno] || formData.turno, complete: true });
    }
    if (formData.tipo_estagio?.trim()) {
      const tipoEstagioLabel =
        formData.tipo_estagio === "S" ? "Sim" : formData.tipo_estagio === "N" ? "Não" : formData.tipo_estagio;
      dadosEscolaresFields.push({ label: "Tipo de estágio é obrigatório?", value: tipoEstagioLabel, complete: true });
    }
    if (formData.orientador_estagio?.trim()) {
      dadosEscolaresFields.push({
        label: "Orientador de Estágio / Responsável pelo Estágio / Coordenador de Estágio ou de Curso (Escola/Faculdade)",
        value: formData.orientador_estagio,
        complete: true,
      });
    }
    if (formData.cargo?.trim()) {
      dadosEscolaresFields.push({ label: "Cargo", value: formData.cargo, complete: true });
    }
    if (formData.email_escola?.trim()) {
      dadosEscolaresFields.push({ label: "E-mail", value: formData.email_escola, complete: true });
    }
    if (formData.cnpj_escola?.trim()) {
      dadosEscolaresFields.push({ label: "CNPJ", value: formData.cnpj_escola, complete: true });
    }
    estudanteSections.push({
      id: "dados-escolares",
      section: "Dados Escolares",
      fields: dadosEscolaresFields,
    });
    return estudanteSections;
  }

  const sections = [];

  if (isMenorDeIdade(idadeCalculada) && isEstudante) {
    sections.push({
      id: "responsavel-legal",
      section: "Responsável Legal",
      fields: [
        {
          label: "Nome do Responsável",
          value: formData.nome_menor_idade,
          complete: !!formData.nome_menor_idade?.trim(),
        },
        {
          label: "Email do Responsável",
          value: formData.email_menor_idade,
          complete: !!formData.email_menor_idade?.trim(),
        },
        {
          label: "Telefone do Responsável",
          value: formatarTelefone(formData.telefone_menor_idade),
          complete: !!formData.telefone_menor_idade?.trim(),
        },
        {
          label: "CPF do Responsável",
          value: formData.cpf_menor_idade,
          complete: !!formData.cpf_menor_idade?.trim(),
        },
      ],
    });
  }

  // Dados da Empresa
  const dadosEmpresaFields = [];
  if (formData.empresa?.trim()) {
    dadosEmpresaFields.push({ label: "Empresa", value: formData.empresa, complete: true });
  }
  if (formData.cnpj_cpf?.trim()) {
    dadosEmpresaFields.push({ label: "CNPJ ou CPF", value: formData.cnpj_cpf, complete: true });
  }
  if (formData.telefones?.trim()) {
    dadosEmpresaFields.push({ label: "Telefones", value: formData.telefones, complete: true });
  }
  if (formData.email_empresa?.trim()) {
    dadosEmpresaFields.push({ label: "Email", value: formData.email_empresa, complete: true });
  }
  if (dadosEmpresaFields.length > 0) {
    sections.push({
      id: "dados-empresa",
      section: "Dados da Empresa",
      fields: dadosEmpresaFields,
    });
  }

  // Unidade
  if (formData.local_realizacao_atividades?.trim()) {
    const unidadeLabel =
      formData.local_realizacao_atividades === "MATRIZ"
        ? "Matriz"
        : formData.local_realizacao_atividades === "FILIAL"
        ? "Filial"
        : formData.local_realizacao_atividades;
    sections.push({
      id: "unidade",
      section: "Unidade",
      fields: [
        {
          label: "Unidade",
          value: unidadeLabel,
          complete: true,
        },
      ],
    });
  }

  // Informações da Filial (somente quando unidade = FILIAL)
  if (formData.local_realizacao_atividades === "FILIAL") {
    const filialFields = [];
    if (formData.nome_filial?.trim()) {
      filialFields.push({ label: "Nome da Filial", value: formData.nome_filial, complete: true });
    }
    if (formData.cpfCnpj?.trim()) {
      filialFields.push({ label: "CPF/CNPJ", value: formData.cpfCnpj, complete: true });
    }
    if (formData.cepFilial?.trim()) {
      filialFields.push({ label: "CEP", value: formData.cepFilial, complete: true });
    }
    if (formData.enderecoFilial?.trim()) {
      filialFields.push({ label: "Endereço", value: formData.enderecoFilial, complete: true });
    }
    if (formData.bairro?.trim()) {
      filialFields.push({ label: "Bairro/Distrito", value: formData.bairro, complete: true });
    }
    if (formData.municipio?.trim()) {
      filialFields.push({ label: "Municipio", value: formData.municipio, complete: true });
    }
    if (formData.uf?.trim()) {
      filialFields.push({ label: "UF", value: formData.uf, complete: true });
    }
    if (formData.telefones?.trim()) {
      filialFields.push({ label: "Telefone 1", value: formData.telefones, complete: true });
    }
    if (formData.telefones2?.trim()) {
      filialFields.push({ label: "Telefone 2", value: formData.telefones2, complete: true });
    }
    if (formData.emailContratos?.trim()) {
      filialFields.push({ label: "E-mail Contrato", value: formData.emailContratos, complete: true });
    }
    if (formData.emailFinanceiro?.trim()) {
      filialFields.push({ label: "E-mail Financeiro", value: formData.emailFinanceiro, complete: true });
    }
    if (filialFields.length > 0) {
      sections.push({
        id: "informacoes-filial",
        section: "Informações da Filial",
        fields: filialFields,
      });
    }
  }

  // Supervisor do Estagiário
  const supervisorFields = [];
  if (formData.nome?.trim()) {
    supervisorFields.push({ label: "Nome", value: formData.nome, complete: true });
  }
  if (formData.cargo_estagiario?.trim()) {
    supervisorFields.push({ label: "Cargo", value: formData.cargo_estagiario, complete: true });
  }
  if (formData.cpf_estagiario?.trim()) {
    supervisorFields.push({ label: "N° do CPF", value: formData.cpf_estagiario, complete: true });
  }
  if (formData.formacao_escolar?.trim()) {
    supervisorFields.push({ label: "Formação Escolar", value: formData.formacao_escolar, complete: true });
  }
  if (formData.email_supervisor?.trim()) {
    supervisorFields.push({ label: "Email", value: formData.email_supervisor, complete: true });
  }
  if (supervisorFields.length > 0) {
    sections.push({
      id: "supervisor-estagiario",
      section: "Supervisor do Estagiário",
      fields: supervisorFields,
    });
  }

  // Quem irá assinar o Termo de Compromisso
  const quemAssinaFields = [];
  if (formData.nome_termo_compromisso?.trim()) {
    quemAssinaFields.push({ label: "Nome", value: formData.nome_termo_compromisso, complete: true });
  }
  if (formData.email?.trim()) {
    quemAssinaFields.push({ label: "Email", value: formData.email, complete: true });
  }
  if (quemAssinaFields.length > 0) {
    sections.push({
      id: "quem-assina-termo",
      section: "Quem irá assinar o Termo de Compromisso? (contrato do estagiário)",
      fields: quemAssinaFields,
    });
  }

  // Dados do(a) Estudante/Estagiário
  const estudanteBlocoFields = [];
  if (formData.cpf_estudante_estagiario?.trim()) {
    estudanteBlocoFields.push({ label: "CPF", value: formData.cpf_estudante_estagiario, complete: true });
  }
  if (formData.origem_candidato?.trim()) {
    estudanteBlocoFields.push({ label: "Origem do Candidato", value: formData.origem_candidato, complete: true });
  }
  if (formData.rg?.trim()) {
    estudanteBlocoFields.push({ label: "RG", value: formData.rg, complete: true });
  }
  if (formData.telefone_estagiario?.trim()) {
    estudanteBlocoFields.push({ label: "Telefone", value: formData.telefone_estagiario, complete: true });
  }
  if (formData.email_estagiario?.trim()) {
    estudanteBlocoFields.push({ label: "Email", value: formData.email_estagiario, complete: true });
  }
  if (estudanteBlocoFields.length > 0) {
    sections.push({
      id: "estudante-estagiario",
      section: "Dados do(a) Estudante/Estagiário",
      fields: estudanteBlocoFields,
    });
  }

  // Dados do Estágio
  const dadosEstagioFields = [];
  if (formData.inicio_estagio?.trim()) {
    dadosEstagioFields.push({ label: "Início", value: formatarDataBR(formData.inicio_estagio), complete: true });
  }
  if (formData.termino_estagio?.trim()) {
    dadosEstagioFields.push({ label: "Término", value: formatarDataBR(formData.termino_estagio), complete: true });
  }
  if (valorBolsaExibicao && valorBolsaExibicao !== "R$ 0,00") {
    dadosEstagioFields.push({ label: "Valor da Bolsa Auxílio", value: valorBolsaExibicao, complete: true });
  }
  if (valorTransporteExibicao && valorTransporteExibicao !== "R$ 0,00") {
    dadosEstagioFields.push({ label: "Valor do Auxílio Transporte", value: valorTransporteExibicao, complete: true });
  }
  if (formData.local_estagio?.trim()) {
    dadosEstagioFields.push({ label: "Local da Realização do Estágio", value: formData.local_estagio, complete: true });
  }
  const nomeEstudante = formData.nome_estagiario || formData.nome_completo;
  if (nomeEstudante?.trim()) {
    dadosEstagioFields.push({ label: "Nome do Estudante", value: nomeEstudante, complete: true });
  }
  if (cpfLimpo && cpfLimpo.length === 11) {
    dadosEstagioFields.push({ label: "CPF", value: cpfLimpo, complete: true });
  }
  if (formData.turno?.trim()) {
    dadosEstagioFields.push({ label: "Turno", value: formData.turno, complete: true });
  }
  if (dadosEstagioFields.length > 0) {
    sections.push({
      id: "dados-estagio",
      section: "Dados do Estágio",
      fields: dadosEstagioFields,
    });
  }

  // Dias e Horários da Realização do Estágio
  const diasHorariosFields = [];
  if (formData.tipo_contrato?.trim()) {
    const tipoContratoLabel =
      {
        PRESENCIAL: "Estágio Presencial",
        HIBRIDO: "Estágio Híbrido",
        HOMEOFFICE: "Estágio Home Office",
      }[formData.tipo_contrato] || formData.tipo_contrato;
    diasHorariosFields.push({ label: "Tipo de Contrato", value: tipoContratoLabel, complete: true });
  }
  if (horario?.tipo) {
    const tipoLabel =
      horario.tipo === "fixo"
        ? "Horário Fixo"
        : horario.tipo === "alternado"
        ? "Horário Alternado"
        : "";
    if (tipoLabel) {
      diasHorariosFields.push({ label: "Horário fixo ou alternado", value: tipoLabel, complete: true });
    }
    if (horario.tipo === "fixo" && horario.fixo) {
      if (horario.fixo.entrada) {
        diasHorariosFields.push({ label: "Horário de entrada", value: horario.fixo.entrada, complete: true });
      }
      if (horario.fixo.saida) {
        diasHorariosFields.push({ label: "Horário de saída", value: horario.fixo.saida, complete: true });
      }
    }
  }
  if (diasHorariosFields.length > 0) {
    sections.push({
      id: "dias-horarios",
      section: "Dias e Horários da Realização do Estágio",
      fields: diasHorariosFields,
    });
  }

  return sections;
};
